<template>
	<div ref="viewport" class="virtual-scroll-table" :style="{ maxHeight }" @scroll="onScroll">
		<table :class="tableClass">
			<thead class="virtual-scroll-table-head">
				<slot name="head" />
			</thead>
			<tbody>
				<tr v-if="topSpacerHeight > 0" class="virtual-scroll-spacer">
					<td :colspan="colspan" :style="{ height: topSpacerHeight + 'px' }"></td>
				</tr>
				<template v-for="(row, i) in visibleRows">
					<slot name="row" :row="row" :index="startIndex + i" />
				</template>
				<tr v-if="bottomSpacerHeight > 0" class="virtual-scroll-spacer">
					<td :colspan="colspan" :style="{ height: bottomSpacerHeight + 'px' }"></td>
				</tr>
			</tbody>
		</table>
	</div>
</template>

<script>
/*
	Renders only the rows near the current scroll position instead of the full `rows`
	array, so a table with many hundreds of rows (and, per row, many columns/inputs)
	doesn't mount a DOM node + watcher per cell for the whole dataset at once.

	Assumes a uniform `rowHeight` per row (the classic fixed-height virtual-scroll
	technique) - the two spacer <tr>s stand in for the rows above/below the rendered
	window so native scrolling/scrollbar size stays correct without measuring every row.
*/
export default {
	props: {
		rows:       { type: Array, required: true },
		rowHeight:  { type: Number, default: 40 },
		overscan:   { type: Number, default: 8 },
		colspan:    { type: Number, default: 1 },
		maxHeight:  { type: String, default: '70vh' },
		tableClass: { type: [String, Array, Object], default: '' },
	},
	data() {
		return {
			scrollTop: 0,
			viewportHeight: 0,
			ticking: false,
		};
	},
	computed: {
		visibleCount() {
			return Math.ceil(this.viewportHeight / this.rowHeight) + this.overscan * 2;
		},
		startIndex() {
			const raw = Math.floor(this.scrollTop / this.rowHeight) - this.overscan;
			return Math.max(0, raw);
		},
		endIndex() {
			return Math.min(this.rows.length, this.startIndex + this.visibleCount);
		},
		visibleRows() {
			return this.rows.slice(this.startIndex, this.endIndex);
		},
		topSpacerHeight() {
			return this.startIndex * this.rowHeight;
		},
		bottomSpacerHeight() {
			return (this.rows.length - this.endIndex) * this.rowHeight;
		},
	},
	mounted() {
		this.measure();
		window.addEventListener('resize', this.measure);
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.measure);
	},
	methods: {
		measure() {
			this.viewportHeight = this.$refs.viewport ? this.$refs.viewport.clientHeight : 0;
		},
		onScroll() {
			if (this.ticking) return;
			this.ticking = true;
			requestAnimationFrame(() => {
				this.scrollTop = this.$refs.viewport ? this.$refs.viewport.scrollTop : 0;
				this.ticking = false;
			});
		},
	},
};
</script>

<style scoped>
.virtual-scroll-table {
	overflow-y: auto;
	overflow-x: auto;
	position: relative;
}
.virtual-scroll-table table {
	width: 100%;
	border-collapse: collapse;
}
.virtual-scroll-table-head {
	position: sticky;
	top: 0;
	z-index: 2;
	background: #fff;
}
.virtual-scroll-spacer td {
	padding: 0 !important;
	border: none !important;
}
</style>
